import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import api from "../services/api";
import { toast } from "react-toastify";
import {
  FaUserGraduate,
  FaClipboardList,
  FaMoneyBillWave,
  FaCashRegister
} from "react-icons/fa";

import EmolaIcon from "../assets/emola.webp";
import MpesaIcon from "../assets/mpesa.png";
import PosIcon from "../assets/pos.jpg";
import CashIcon from "../assets/cash.png";
import caixa from "../assets/cash.jpg";

import {
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line
} from "recharts";

/* ================= ANIMAÇÃO ================= */

const rotate = keyframes`
0%{transform:rotate(0deg);}
100%{transform:rotate(360deg);}
`;

const countAnimation = (start, end, duration, setValue) => {
  let startTimestamp = null;

  const step = (timestamp) => {
    if (!startTimestamp) startTimestamp = timestamp;

    const progress = Math.min((timestamp - startTimestamp) / duration, 1);

    setValue(Math.floor(progress * (end - start) + start));

    if (progress < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
};

/* ================= HELPERS ================= */

const formatDate = (date) => {
  if (!date) return null;
  return new Date(date).toISOString().slice(0,10);
};

const formatMoney = (value) => {
  const num = Number(value);
  if (isNaN(num)) return "0,00 MT";

  return num.toLocaleString("pt-MZ", {
    style: "currency",
    currency: "MZN"
  });
};

/* ================= STYLE ================= */

const Container = styled.div`
padding:30px;
background:#f5f7fa;
min-height:100vh;
`;

const Header = styled.h2`
margin-bottom:25px;
`;

const Grid = styled.div`
display:grid;
grid-template-columns:repeat(auto-fit,minmax(250px,1fr));
gap:25px;
`;

const Card = styled.div`
background:white;
border-radius:18px;
padding:25px;
box-shadow:0px 8px 20px rgba(0,0,0,0.05);
`;

const CardIcon = styled.div`
font-size:28px;
color:${p=>p.color};
`;

const CardTitle = styled.div`
font-size:14px;
color:#777;
margin-top:10px;
`;

const CardValue = styled.div`
font-size:26px;
font-weight:700;
margin-top:5px;
`;

const CardFooter = styled.div`
font-size:12px;
color:#999;
margin-top:10px;
`;

const CircleChart = styled.div`
width:60px;
height:60px;
border-radius:50%;
border:6px solid #eee;
border-top-color:${p=>p.color};
animation:${rotate} 3s linear infinite;
margin-left:auto;
`;

const Loading = styled.div`
text-align:center;
padding:40px;
`;

/* ================= COMPONENT ================= */

export default function DashboardModern(){

const [totalAlunos,setTotalAlunos]=useState(0);
const [inscricoesHoje,setInscricoesHoje]=useState(0);
const [pagamentosHoje,setPagamentosHoje]=useState(0);
const [valorHoje,setValorHoje]=useState(0);
const [totalGeral,setTotalGeral]=useState(0);
const [porForma,setPorForma]=useState({});
const [caixasAbertos,setCaixasAbertos]=useState(0);
const [totalMes,setTotalMes]=useState(0);
const [pagamentosMes,setPagamentosMes]=useState(0);
const [evolucao,setEvolucao]=useState([]);

const [loading,setLoading]=useState(true);

/* ================= LOAD ================= */

const loadDashboard = async () => {

try{

setLoading(true);

const [alunosRes, inscRes, pagRes, caixaRes, evolucaoRes] = await Promise.all([
  api.get("/alunos"),
  api.get("/inscricoes"),
  api.get("/pagamentos/resumo"),
  api.get("/caixa"),
  api.get("/pagamentos/evolucao")
]);

const hoje = formatDate(new Date());

const inscricoesHojeList = inscRes.data.filter(
i => formatDate(i.data_inscricao) === hoje
);

const {
  totalHoje = 0,
  quantidadeHoje = 0,
  totalGeral = 0,
  porForma = {},
  totalMes = 0,
  quantidadeMes = 0
} = pagRes.data;

const caixasAbertosCount = caixaRes.data.filter(
c => c.status === "ABERTO"
).length;

countAnimation(0, alunosRes.data.length, 1200, setTotalAlunos);
countAnimation(0, inscricoesHojeList.length, 1200, setInscricoesHoje);
countAnimation(0, quantidadeHoje, 1200, setPagamentosHoje);
countAnimation(0, quantidadeMes, 1200, setPagamentosMes);
countAnimation(0, caixasAbertosCount, 1200, setCaixasAbertos);

setValorHoje(totalHoje);
setTotalGeral(totalGeral);
setPorForma(porForma);
setTotalMes(totalMes);

setEvolucao(
  evolucaoRes.data.map(e=>({
    data:e.data,
    valor:Number(e.total)
  }))
);

}catch(err){
console.log(err);
toast.error("Erro ao carregar painel");
}finally{
setLoading(false);
}
};

useEffect(()=>{loadDashboard();},[]);

/* ================= CHART DATA ================= */

const chartData = Object.entries(porForma).map(([forma,valores])=>({
  name:forma,
  value:Number(valores?.mes||0)
}));

const barData = Object.entries(porForma).map(([forma,valores])=>({
  name:forma,
  valor:Number(valores?.mes||0)
}));

const COLORS=["#22c55e","#f97316","#3b82f6","#e11d48","#a855f7"];

/* ================= ICON ================= */

const getIcon = (forma) => {
  switch (forma) {
    case "MPESA": return MpesaIcon;
    case "EMOLA": return EmolaIcon;
    case "POS": return PosIcon;
    default: return CashIcon;
  }
};

/* ================= RENDER ================= */

if(loading){
return(<Container><Loading>Carregando...</Loading></Container>);
}

return(
<Container>

<Header>Painel do Administrador</Header>

<Grid>

<Card>
<CardIcon color="#3b82f6"><FaUserGraduate/></CardIcon>
<CardTitle>Total de Alunos</CardTitle>
<CardValue>{totalAlunos}</CardValue>
<CardFooter>Registros</CardFooter>
<CircleChart color="#3b82f6"/>
</Card>

<Card>
<CardIcon color="#10b981"><FaClipboardList/></CardIcon>
<CardTitle>Inscrições Hoje</CardTitle>
<CardValue>{inscricoesHoje}</CardValue>
<CircleChart color="#10b981"/>
</Card>

<Card>
<CardIcon color="#f59e0b"><FaMoneyBillWave/></CardIcon>
<CardTitle>Pagamentos Hoje</CardTitle>
<CardValue>{pagamentosHoje}</CardValue>
<CardFooter>{formatMoney(valorHoje)}</CardFooter>
<CircleChart color="#f59e0b"/>
</Card>

<Card>
<CardIcon color="#22c55e"><FaMoneyBillWave/></CardIcon>
<CardTitle>Pagamentos Mês</CardTitle>
<CardValue>{pagamentosMes}</CardValue>
<CardFooter>{formatMoney(totalMes)}</CardFooter>
<CircleChart color="#22c55e"/>
</Card>

<Card>
<CardIcon color="#6366f1"><FaMoneyBillWave/></CardIcon>
<CardTitle>Total Geral</CardTitle>
<CardValue>{formatMoney(totalGeral)}</CardValue>
<CircleChart color="#6366f1"/>
</Card>

<Card>
<CardIcon color="#ef4444"><img src={caixa} width="60"/></CardIcon>
<CardTitle>Caixas Abertos</CardTitle>
<CardValue>{caixasAbertos}</CardValue>
<CircleChart color="#ef4444"/>
</Card>

{Object.entries(porForma).map(([forma,valores],i)=>(
<Card key={i}>
<CardIcon>
<img src={getIcon(forma)} width="60"/>
</CardIcon>
<CardTitle>{forma}</CardTitle>
<CardValue>{formatMoney(valores?.hoje)}</CardValue>
<CardFooter>Mês: {formatMoney(valores?.mes)}</CardFooter>
<CircleChart color="#0ea5e9"/>
</Card>
))}

<Card style={{gridColumn:"span 2"}}>
<CardTitle>Distribuição (Mês)</CardTitle>

<ResponsiveContainer width="100%" height={300}>
<PieChart>
<Pie
data={chartData}
innerRadius={60}
outerRadius={110}
paddingAngle={4}
dataKey="value"
>
{chartData.map((e,i)=>(
<Cell key={i} fill={COLORS[i%COLORS.length]} stroke="#fff" strokeWidth={3}/>
))}
</Pie>
<Tooltip formatter={(v)=>formatMoney(v)}/>
<Legend/>
</PieChart>
</ResponsiveContainer>
</Card>

<Card style={{gridColumn:"span 2"}}>
<CardTitle>Comparação</CardTitle>

<ResponsiveContainer width="100%" height={300}>
<BarChart data={barData}>
<CartesianGrid strokeDasharray="3 3" opacity={0.2}/>
<XAxis dataKey="name"/>
<YAxis/>
<Tooltip formatter={(v)=>formatMoney(v)}/>
<Bar dataKey="valor" radius={[10,10,0,0]} fill="#3b82f6"/>
</BarChart>
</ResponsiveContainer>
</Card>

<Card style={{gridColumn:"span 2"}}>
<CardTitle>Evolução (Forex Style)</CardTitle>

<ResponsiveContainer width="100%" height={300}>
<LineChart data={evolucao}>
<CartesianGrid strokeDasharray="3 3" opacity={0.1}/>
<XAxis dataKey="data"/>
<YAxis/>
<Tooltip formatter={(v)=>formatMoney(v)}/>
<Line
type="monotone"
dataKey="valor"
stroke="#22c55e"
strokeWidth={3}
dot={{r:4}}
activeDot={{r:7}}
/>
</LineChart>
</ResponsiveContainer>
</Card>

</Grid>
</Container>
);
}